import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectMembers, selectTotalMonthlyDebt } from '@/utils/store';
import { useCurrency } from '@/hooks/useCurrency';
import { formatCurrency as formatCurrencyRaw } from '@/utils/format';
import { CATEGORY_ICONS, CATEGORY_COLORS } from '@/utils/format';
import PageHeader from '@/components/shared/PageHeader';
import { ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, XCircle, TrendingUp, TrendingDown, Landmark, Target, Users, PieChart, CreditCard } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, isWithinInterval, parseISO } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

export default function MonthlyXray() {
  const { t, i18n } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const locale = i18n.language === 'es' ? es : enUS;

  const [currentDate, setCurrentDate] = useState(new Date());
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthKey = format(currentDate, 'yyyy-MM');

  const transactions = useStore((s) => s.transactions);
  const incomeUnits = useStore((s) => s.incomeUnits);
  const budgets = useStore((s) => s.budgets);
  const credits = useStore((s) => s.credits);
  const goals = useStore((s) => s.goals);
  const members = useStore(selectMembers);
  const totalMonthlyDebt = useStore(selectTotalMonthlyDebt);

  // Filter transactions by selected month
  const monthTransactions = useMemo(() =>
    transactions.filter((tx) => {
      const date = parseISO(tx.date);
      return isWithinInterval(date, { start: monthStart, end: monthEnd });
    }),
    [transactions, monthStart, monthEnd]
  );

  const monthIncome = useMemo(() =>
    monthTransactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0),
    [monthTransactions]
  );

  const monthExpenses = useMemo(() =>
    monthTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0),
    [monthTransactions]
  );

  const balance = monthIncome - monthExpenses;
  const spentRatio = monthIncome > 0 ? Math.round((monthExpenses / monthIncome) * 100) : 0;

  // Verdict status
  const verdictStatus = useMemo(() => {
    if (monthIncome === 0) return 'noData';
    const margin = ((monthIncome - monthExpenses) / monthIncome) * 100;
    if (margin > 30) return 'great';
    if (margin > 0) return 'ok';
    if (margin > -10) return 'warning';
    return 'danger';
  }, [monthIncome, monthExpenses]);

  // Income units for this month
  const monthUnits = useMemo(() =>
    incomeUnits.filter((u) => u.month === monthKey).map((unit) => {
      const totalFixed = unit.fixedCosts.reduce((sum, c) => sum + c.amount, 0);
      const totalVariable = transactions
        .filter((tx) => tx.unitId === unit.id && tx.category === 'unit_cost')
        .reduce((sum, tx) => sum + tx.amount, 0);
      const netIncome = unit.grossIncome - totalFixed - totalVariable;
      return { unit, totalFixed, totalVariable, netIncome };
    }),
    [incomeUnits, monthKey, transactions]
  );

  // Simple income transactions (not linked to income units)
  const simpleIncomes = useMemo(() =>
    monthTransactions.filter((tx) => tx.type === 'income' && !tx.unitId),
    [monthTransactions]
  );

  // Expenses by category
  const expensesByCategory = useMemo(() => {
    const map = {};
    monthTransactions
      .filter((tx) => tx.type === 'expense')
      .forEach((tx) => {
        map[tx.category] = (map[tx.category] || 0) + tx.amount;
      });
    return Object.entries(map)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [monthTransactions]);

  const maxCategoryAmount = expensesByCategory[0]?.amount || 1;

  // Budget summary
  const budgetSummary = useMemo(() => {
    let ok = 0, warning = 0, exceeded = 0;
    budgets.forEach((b) => {
      const pct = b.limit > 0 ? (b.spent / b.limit) * 100 : 0;
      if (pct >= 100) exceeded++;
      else if (pct >= 80) warning++;
      else ok++;
    });
    return { ok, warning, exceeded };
  }, [budgets]);

  // Active credits
  const activeCredits = useMemo(() =>
    credits.filter((c) => c.paidInstallments < c.totalInstallments),
    [credits]
  );

  const debtRatio = monthIncome > 0 ? Math.round((totalMonthlyDebt / monthIncome) * 100) : 0;

  // Expenses by member
  const memberExpenses = useMemo(() => {
    const map = {};
    monthTransactions
      .filter((tx) => tx.type === 'expense' && tx.memberIds?.length > 0)
      .forEach((tx) => {
        tx.memberIds.forEach((id) => {
          map[id] = (map[id] || 0) + tx.amount;
        });
      });
    return Object.entries(map)
      .map(([id, amount]) => ({
        member: members.find((m) => m.id === id),
        amount,
      }))
      .filter((e) => e.member)
      .sort((a, b) => b.amount - a.amount);
  }, [monthTransactions, members]);

  const maxMemberAmount = memberExpenses[0]?.amount || 1;

  const prevMonth = () => setCurrentDate((d) => subMonths(d, 1));
  const nextMonth = () => setCurrentDate((d) => addMonths(d, 1));

  const verdictConfig = {
    great: { icon: CheckCircle2, color: 'text-success-600 dark:text-success-400', bg: 'from-success-500 to-emerald-600', barColor: 'bg-success-500' },
    ok: { icon: CheckCircle2, color: 'text-success-600 dark:text-success-400', bg: 'from-brand-500 to-brand-700', barColor: 'bg-brand-500' },
    warning: { icon: AlertTriangle, color: 'text-warning-600 dark:text-warning-400', bg: 'from-warning-500 to-orange-600', barColor: 'bg-warning-500' },
    danger: { icon: XCircle, color: 'text-danger-600 dark:text-danger-400', bg: 'from-danger-500 to-red-700', barColor: 'bg-danger-500' },
    noData: { icon: CheckCircle2, color: 'text-gray-400', bg: 'from-gray-400 to-gray-500', barColor: 'bg-gray-400' },
  };

  const verdict = verdictConfig[verdictStatus];
  const VerdictIcon = verdict.icon;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with month nav */}
      <div className="flex items-center justify-between">
        <PageHeader title={t('xray.title')} hint={t('xray.hint')} />
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <ChevronLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] text-center capitalize">
            {format(currentDate, 'MMMM yyyy', { locale })}
          </span>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Verdict card */}
      <div className={`card bg-gradient-to-br ${verdict.bg} text-white border-0 p-6`}>
        <div className="flex items-center gap-3 mb-3">
          <VerdictIcon size={28} />
          <div>
            {verdictStatus === 'noData' ? (
              <h2 className="text-lg font-bold">{t('xray.noData')}</h2>
            ) : balance >= 0 ? (
              <h2 className="text-lg font-bold">
                {t('xray.verdict.cuadrado')} — {t('xray.verdict.surplus')} {formatCurrency(balance)}
              </h2>
            ) : (
              <h2 className="text-lg font-bold">
                {t('xray.verdict.missing')} {formatCurrency(Math.abs(balance))}
              </h2>
            )}
            <p className="text-sm text-white/80">{t(`xray.verdict.${verdictStatus}`)}</p>
          </div>
        </div>
        <div className="flex gap-4 text-sm text-white/90 mb-3">
          <span>{t('dashboard.income')}: <strong>{formatCurrency(monthIncome)}</strong></span>
          <span>{t('dashboard.expenses')}: <strong>{formatCurrency(monthExpenses)}</strong></span>
          <span>{t('dashboard.balance')}: <strong>{formatCurrency(balance)}</strong></span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white/20 rounded-full h-3">
            <div
              className="h-3 rounded-full bg-white/80 transition-all"
              style={{ width: `${Math.min(100, spentRatio)}%` }}
            />
          </div>
          <span className="text-sm font-semibold">{spentRatio}% {t('xray.spentRatio')}</span>
        </div>
      </div>

      {/* Income section */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-success-500" />
          <h2 className="font-semibold text-gray-900 dark:text-white">{t('xray.income')}</h2>
        </div>
        {monthUnits.length > 0 && (
          <div className="space-y-2 mb-3">
            {monthUnits.map(({ unit, netIncome }) => (
              <div key={unit.id} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{unit.icon}</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{unit.name}</span>
                  <span className="text-xs text-gray-400">{unit.currency}</span>
                </div>
                <span className="text-sm font-semibold text-success-600 dark:text-success-400">
                  {formatCurrencyRaw(netIncome, unit.currency)}
                </span>
              </div>
            ))}
          </div>
        )}
        {simpleIncomes.length > 0 && (
          <div className="space-y-2 mb-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('xray.simpleIncome')}</p>
            {simpleIncomes.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">{tx.description}</span>
                <span className="text-sm font-medium text-success-600 dark:text-success-400">{formatCurrency(tx.amount)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-gray-100 dark:border-gray-800 pt-3 flex justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('xray.totalIncome')}</span>
          <span className="font-bold text-success-600 dark:text-success-400">{formatCurrency(monthIncome)}</span>
        </div>
      </div>

      {/* Expenses section */}
      {expensesByCategory.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown size={18} className="text-danger-500" />
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('xray.expenses')}</h2>
          </div>
          <div className="space-y-3">
            {expensesByCategory.map(({ category, amount }) => {
              const pct = monthIncome > 0 ? Math.round((amount / monthIncome) * 100) : 0;
              const barWidth = Math.round((amount / maxCategoryAmount) * 100);
              return (
                <div key={category}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span>{CATEGORY_ICONS[category] || '📌'}</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t(`categories.${category}`)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(amount)}</span>
                      <span className="text-xs text-gray-400">{pct}%</span>
                    </div>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${barWidth}%`, backgroundColor: CATEGORY_COLORS[category] || '#94a3b8' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-3 mt-4 flex justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('xray.totalExpenses')}</span>
            <span className="font-bold text-danger-600 dark:text-danger-400">{formatCurrency(monthExpenses)}</span>
          </div>
        </div>
      )}

      {/* Budgets section */}
      {budgets.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <PieChart size={18} className="text-brand-500" />
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('xray.budgets')}</h2>
          </div>
          <div className="flex flex-wrap gap-3 mb-4">
            {budgetSummary.ok > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400">
                <CheckCircle2 size={12} /> {budgetSummary.ok} {t('xray.budgetsOk')}
              </span>
            )}
            {budgetSummary.warning > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-400">
                <AlertTriangle size={12} /> {budgetSummary.warning} {t('xray.budgetsWarning')}
              </span>
            )}
            {budgetSummary.exceeded > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400">
                <XCircle size={12} /> {budgetSummary.exceeded} {t('xray.budgetsExceeded')}
              </span>
            )}
          </div>
          <div className="space-y-3">
            {budgets.map((b) => {
              const pct = b.limit > 0 ? Math.round((b.spent / b.limit) * 100) : 0;
              const barColor = pct >= 100 ? 'bg-danger-500' : pct >= 80 ? 'bg-warning-500' : 'bg-success-500';
              return (
                <div key={b.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span>{CATEGORY_ICONS[b.category] || '📌'}</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t(`categories.${b.category}`)}</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatCurrency(b.spent)} / {formatCurrency(b.limit)}
                    </span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Debts section */}
      {activeCredits.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={18} className="text-purple-500" />
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('xray.debt')}</h2>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('xray.monthlyDebt')}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(totalMonthlyDebt)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('xray.debtRatio')}</p>
              <p className={`text-lg font-bold ${debtRatio > 40 ? 'text-danger-600 dark:text-danger-400' : debtRatio > 25 ? 'text-warning-600 dark:text-warning-400' : 'text-success-600 dark:text-success-400'}`}>
                {debtRatio}%
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('xray.activeCredits')}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{activeCredits.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Goals section */}
      {goals.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} className="text-cyan-500" />
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('xray.goals')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {goals.map((goal) => {
              const pct = goal.target > 0 ? Math.round((goal.saved / goal.target) * 100) : 0;
              return (
                <div key={goal.id} className="flex items-center gap-3">
                  <span className="text-xl">{goal.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{goal.name}</span>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-2">{pct}%</span>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-success-500' : 'bg-cyan-500'}`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Family section */}
      {memberExpenses.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-pink-500" />
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('xray.family')}</h2>
          </div>
          <div className="space-y-3">
            {memberExpenses.map(({ member, amount }) => {
              const barWidth = Math.round((amount / maxMemberAmount) * 100);
              return (
                <div key={member.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                        style={{ backgroundColor: member.color }}
                      >
                        {member.initials}
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{member.name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(amount)}</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${barWidth}%`, backgroundColor: member.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
